import { arenaTemplateDefaults } from '../../../lib/s3/defaults';
import {
  ARENA_EDGES,
  ARENA_OBJECTIVE_KINDS,
  LURE_CATEGORIES,
  type ArenaPointDef,
  type ArenaRectDef,
  type ArenaSpawnDef,
  type ArenaTemplateDef,
} from '../../../lib/s3/types';
import { withDefaults } from '../../../lib/teamsDefaults';
import type { FieldErrors } from '../../../lib/validate';
import {
  CheckboxField,
  Field,
  Fieldset,
  JsonListField,
  NumberInput,
  SelectField,
  TextField,
} from '../fields';
import { IdListEditor } from '../s2/kv';
import type { FormProps } from '../types';
import { ArenaPreview } from './ArenaPreview';

const opts = (xs: readonly string[]) => xs.map((x) => ({ value: x, label: x }));

/** An `{x, y}` pair of number inputs with the issues at `path`, `path.x` and `path.y`. */
function PointField({
  label,
  value,
  onChange,
  errors,
  path,
}: {
  label: string;
  value: ArenaPointDef | null | undefined;
  onChange: (p: ArenaPointDef) => void;
  errors: FieldErrors;
  path: string;
}) {
  const p = value ?? { x: 0, y: 0 };
  return (
    <Field
      label={label}
      issues={[
        ...(errors[path] ?? []),
        ...(errors[`${path}.x`] ?? []),
        ...(errors[`${path}.y`] ?? []),
      ]}
    >
      {() => (
        <div className="grid grid-cols-2 gap-1">
          <NumberInput
            label={`${label} x`}
            value={p.x}
            onChange={(x) => onChange({ ...p, x: x ?? 0 })}
          />
          <NumberInput
            label={`${label} y`}
            value={p.y}
            onChange={(y) => onChange({ ...p, y: y ?? 0 })}
          />
        </div>
      )}
    </Field>
  );
}

/**
 * An arena template (lurerun.html "Templates fitted to the place"): the preview with the shortest legal run on top, then the fields.
 * Geometry lists (blocks, slots, spawn shares) are JSON lists, as the templates are laid out on paper and pasted in; the whole entry is
 * also editable in Edit JSON. Coordinates are metres from the top-left corner; points are `{x, y}` and rects `{x, y, w, h, kind}`.
 */
export function ArenaTemplateForm({ value, onChange, errors }: FormProps<ArenaTemplateDef>) {
  const t = withDefaults(arenaTemplateDefaults(), value) as Required<ArenaTemplateDef>;
  const set = <K extends keyof ArenaTemplateDef>(k: K, x: ArenaTemplateDef[K]) =>
    onChange({ ...t, [k]: x });
  const size = t.sizeM ?? [];
  const exit = t.exit ?? { x: 0, y: 0, w: 0, h: 0, kind: '' };
  const edges = new Set(t.reinforceFrom ?? []);
  const under = (prefix: string) =>
    Object.entries(errors)
      .filter(([k]) => k === prefix || k.startsWith(`${prefix}.`))
      .flatMap(([, v]) => v ?? []);
  return (
    <>
      <ArenaPreview template={t} />
      <Fieldset legend="Basics">
        <TextField label="Id" mono value={t.id} issues={errors.id} onChange={(x) => set('id', x)} />
        <TextField
          label="Name"
          value={t.name}
          issues={errors.name}
          onChange={(x) => set('name', x)}
        />
        <IdListEditor
          label="Categories"
          base="categories"
          what="category"
          hint="Building categories this template fits."
          options={opts(LURE_CATEGORIES)}
          value={t.categories}
          errors={errors}
          onChange={(x) => set('categories', x)}
        />
        <Field label="Size (m)" issues={errors.sizeM}>
          {() => (
            <div className="grid grid-cols-2 gap-1">
              <NumberInput
                label="Width m"
                min={0}
                value={size[0]}
                onChange={(x) => set('sizeM', [x ?? 0, size[1] ?? 0])}
              />
              <NumberInput
                label="Depth m"
                min={0}
                value={size[1]}
                onChange={(y) => set('sizeM', [size[0] ?? 0, y ?? 0])}
              />
            </div>
          )}
        </Field>
        <Field label="Cell size (m)" issues={errors.cellM}>
          {(id, invalid) => (
            <NumberInput
              id={id}
              invalid={invalid}
              min={0}
              value={t.cellM}
              onChange={(x) => set('cellM', x ?? 0)}
            />
          )}
        </Field>
      </Fieldset>
      <Fieldset legend="Objective, start, exit">
        <SelectField
          label="Objective kind"
          value={t.objective?.kind}
          options={opts(ARENA_OBJECTIVE_KINDS)}
          issues={errors['objective.kind']}
          onChange={(k) => set('objective', { ...t.objective, kind: k ?? '' })}
        />
        <PointField
          label="Objective at"
          path="objective.at"
          value={t.objective?.at}
          errors={errors}
          onChange={(p) => set('objective', { ...t.objective, at: p })}
        />
        <PointField
          label="Start"
          path="start"
          value={t.start}
          errors={errors}
          onChange={(p) => set('start', p)}
        />
        <Field label="Exit (x, y, w, h)" issues={under('exit')}>
          {() => (
            <div className="grid grid-cols-4 gap-1">
              {(['x', 'y', 'w', 'h'] as const).map((k) => (
                <NumberInput
                  key={k}
                  label={`Exit ${k}`}
                  value={exit[k]}
                  onChange={(v) => set('exit', { ...exit, [k]: v ?? 0 })}
                />
              ))}
            </div>
          )}
        </Field>
        <div className="sm:col-span-2">
          <span className="block text-xs font-semibold uppercase tracking-wide">
            Reinforcements from
          </span>
          <div className="flex flex-wrap gap-3">
            {ARENA_EDGES.map((e) => (
              <CheckboxField
                key={e}
                label={e}
                checked={edges.has(e)}
                onChange={(on) =>
                  set(
                    'reinforceFrom',
                    on
                      ? [...(t.reinforceFrom ?? []), e]
                      : (t.reinforceFrom ?? []).filter((x) => x !== e),
                  )
                }
              />
            ))}
          </div>
        </div>
      </Fieldset>
      <Fieldset legend="Geometry">
        <JsonListField<ArenaRectDef>
          label="Blocks"
          what="block"
          hint="Generic commercial shells, never houses: {x, y, w, h, kind}."
          value={t.blocks}
          issues={under('blocks')}
          make={() => ({ x: 0, y: 0, w: 20, h: 20, kind: 'shop' })}
          onChange={(x) => set('blocks', x)}
        />
        <JsonListField<ArenaPointDef>
          label="Wreck slots"
          what="wreck slot"
          value={t.wreckSlots}
          issues={under('wreckSlots')}
          make={() => ({ x: 0, y: 0 })}
          onChange={(x) => set('wreckSlots', x)}
        />
        <JsonListField<ArenaPointDef>
          label="Fuel can slots"
          what="fuel can slot"
          hint="2–4 are used per run; never inside a block."
          value={t.fuelCanSlots}
          issues={under('fuelCanSlots')}
          make={() => ({ x: 0, y: 0 })}
          onChange={(x) => set('fuelCanSlots', x)}
        />
        <JsonListField<ArenaSpawnDef>
          label="Horde spawn"
          what="spawn share"
          hint="Shares sum to 1: around a point (radiusM), or along the streets."
          value={t.spawn}
          issues={under('spawn')}
          make={() => ({ around: null, radiusM: 0, streets: true, share: 0.3 })}
          onChange={(x) => set('spawn', x)}
        />
      </Fieldset>
    </>
  );
}
